import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { GAME_SYMBOL_DEADLOCK } from "@/game-deadlock/definition-symbol.mjs";
import type { AbilitiesItems } from "@/game-deadlock/models/abilitiesItems.mjs";
import type { Heroes } from "@/game-deadlock/models/hero.mjs";
import type { DeadlockHeroStats } from "@/game-deadlock/models/heroesStats.mjs";
import type { HeroItemStats } from "@/game-deadlock/models/heroItemStats.mjs";
import type { Match } from "@/game-deadlock/models/match.mjs";
import type {
  Matchlist,
  MatchlistMeta,
} from "@/game-deadlock/models/matchlist.mjs";
import type { Player } from "@/game-deadlock/models/player.mjs";
import type { ProfileHeroStats } from "@/game-deadlock/models/profileHeroStats.mjs";
import type { ProfileStats } from "@/game-deadlock/models/profileStats.mjs";
import type { Steam } from "@/game-deadlock/models/steamAccount.mjs";
import routes from "@/game-deadlock/routes.mjs";
import GameIconBoxDeadlock from "@/inline-assets/game-icon-box-deadlock.svg";
import GameIconShapeDeadlock from "@/inline-assets/game-icon-shape-deadlock.svg";

export { GAME_SYMBOL_DEADLOCK };

const DEADLOCK_OVERLAY_SETTINGS = {
  isCombatOverlayEnabled: true,
  preferredMatchTab: "",
};

const definition = {
  gameSymbol: GAME_SYMBOL_DEADLOCK,
  friendlyName: ["common:games.deadlock.long", "Deadlock"],
  fullName: ["common:games.deadlock.long", "Deadlock"],
  shortName: "deadlock" as const,
  gameColor: "#EFDDBE",
  imageBanner: `${appURLs.CDN}/blitz/library/banner-deadlock-v2.webp`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/deadlock-small.webp`,
  iconShape: GameIconShapeDeadlock,
  iconBox: GameIconBoxDeadlock,
  iconBadge: GameIconBoxDeadlock,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/deadlock-small.svg`,
  isActive: true as const,
  preferFriendlyName: false as const,
  meta: {
    bg: "deadlock-bg.webp",
    players: "150K+",
    matches: "3M+",
  },
  settings: () => import("@/game-deadlock/Settings.jsx"),
  IPC: () => import("@/game-deadlock/init.mjs"),
  navLinks: [
    {
      href: "/deadlock/heroes",
      text: ["deadlock:heroesStats", "Heroes Stats"],
    },
    {
      href: "/deadlock/tier-list",
      text: ["common:tierlist", "Tier List"],
    },
    // {
    //   href: "/overlays/deadlock",
    //   text: ["common:navigation.overlays", "Overlays"],
    //   excludeSearch: true,
    // },
    {
      href: `/deadlock/faq`,
      text: ["common:howItWorks", "How It Works"],
    },
  ],
  routes,
  promoFeatures: [
    {
      key: "deadlock-faq-how-it-works",
      date: new Date("2024-09-23"),
      image: `${appURLs.CDN}/blitz/deadlock/misc/faq/deadlock-promo-logo.webp`,
      title: ["deadlock:announcement.deadlockForBlitz", "Deadlock for Blitz!"],
      description: [
        "deadlock:announcement.deadlockForBlitzDescription",
        "Deadlock now works with Blitz! Read more on how to start tracking your progress!",
      ],
      link: "/deadlock/faq",
      linkText: ["common:startTracking", "Start Tracking"],
    },
  ],
  overlays: {
    // combat: {
    //   key: ["isCombatOverlayEnabled"],
    //   title: ["overlays:dynamicStats.title", "Dynamic Stats"],
    //   tags: ["tracker"],
    //   description: [
    //     "overlays:dynamicStats.desc",
    //     "Track key combat metrics to better gauge performance in real time.",
    //   ],
    //   notification: [
    //     "overlays:csgo.dynamicStats.notification",
    //     "This overlay only works in Windowed mode.",
    //   ],
    //   brief: [
    //     "overlays:dynamicStats.brief",
    //     "Track key combat metrics to better gauge performance in real time.",
    //   ],
    //   images: {
    //     large: `${appURLs.CDN}/blitz/ui/img/settings/deadlock/1000x880.webp`,
    //     small: `${appURLs.CDN}/blitz/ui/img/settings/deadlock/840x486.webp`,
    //     featured: `${appURLs.CDN}/blitz/ui/img/settings/deadlock/1016x1240.webp`,
    //   },
    //   native: true,
    //   date: new Date(500),
    // },
  },
  overlaySettingsKey: "deadlockOverlaySettings",
  overlaySettings: DEADLOCK_OVERLAY_SETTINGS,
} satisfies GameDefinition<typeof GAME_SYMBOL_DEADLOCK>;

export default definition;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    deadlock: {
      heroes: Heroes;
      abilitiesItems: AbilitiesItems;
      abilitiesItemsLocal: Record<string, Record<string, string>>;
      heroesStats: DeadlockHeroStats[];
      heroItemsStats: Record<string, HeroItemStats[]>;
      steam: Record<string, Steam>; // :steamId
      profiles: Record<string, Player | MatchlistMeta["player"]>; // :steamId
      profileStats: Record<string, ProfileStats>; // :steamId
      profileHeroStats: Record<string, ProfileHeroStats>; // :steamId
      matches: Record<string, Match>; // :matchId
      matchlist: Record<string, Matchlist>; // :steamId
      matchlistMeta: Record<string, Record<string, MatchlistMeta>>; // :steamId, :matchId
      liveGame: null;
    };
  }
}
